import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@radix-ui/react-icons';
import { ASSETS } from '../../../../constants/assets';
import {
  formatSuggestedByName,
  mapSuggestedProfileRow,
  suggestionService,
} from '../../../../services/suggestionService';
import { useAuthStore } from '../../../../store/authStore';
import type {
  PendingSuggestionRow,
  SuggestionApprovalStatus,
  SuggestionTab,
} from '../../../../types/suggestion';
import {
  canReviewSuggestions,
  canSendSuggestions,
  isRelationshipManager,
} from '../../../../utils/suggestionPermissions';
import { SuggestProfileModal } from '../SuggestProfileModal';
import { SubscriptionToast } from '../../../plans/components/SubscriptionToast';
import './RmSuggestionsTab.scss';

interface RmSuggestionsTabProps {
  clientUserId: string;
  clientName: string;
}

interface ToastState {
  title: string;
  message: string;
}

const TABS: { key: SuggestionTab; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

const formatSuggestionDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const toastForApprovalStatus = (status: SuggestionApprovalStatus): ToastState => {
  if (status === 'pending') {
    return {
      title: 'Submitted for approval',
      message: 'Your suggestions will be reviewed before the client is notified.',
    };
  }
  return {
    title: 'Suggestions sent',
    message: 'The client will be notified by email.',
  };
};

const emptyMessageForTab = (tab: SuggestionTab, isRm: boolean): string => {
  if (tab === 'pending') {
    return isRm
      ? 'No pending suggestions. Use Suggest New Profile to submit matches for review.'
      : 'No pending suggestions awaiting approval.';
  }
  if (tab === 'approved') {
    return 'No approved suggestions yet.';
  }
  return 'No rejected suggestions.';
};

export const RmSuggestionsTab: React.FC<RmSuggestionsTabProps> = ({
  clientUserId,
  clientName,
}) => {
  const navigate = useNavigate();
  const role = useAuthStore((state) => state.role);
  const canSend = canSendSuggestions(role);
  const canReview = canReviewSuggestions(role);
  const isRm = isRelationshipManager(role);

  const [activeTab, setActiveTab] = useState<SuggestionTab>('pending');
  const [isSuggestModalOpen, setIsSuggestModalOpen] = useState(false);
  const [rows, setRows] = useState<PendingSuggestionRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  const loadSuggestions = useCallback(async () => {
    if (!clientUserId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await suggestionService.searchClientSuggestions(clientUserId, {
        approval_status: activeTab,
        page: 1,
        limit: 50,
      });
      setRows(response.data);
      setSelectedIds(new Set());
    } catch (err) {
      setError(
        (err as { message?: string })?.message ?? 'Failed to load suggestions'
      );
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, clientUserId]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  const toggleSelect = useCallback((userId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  const pendingUserIds = rows.map((row) => Number(row.suggestedProfile.userId));
  const allPendingSelected =
    pendingUserIds.length > 0 && pendingUserIds.every((id) => selectedIds.has(id));
  const somePendingSelected = pendingUserIds.some((id) => selectedIds.has(id));

  const toggleSelectAll = useCallback(() => {
    if (allPendingSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(pendingUserIds));
  }, [allPendingSelected, pendingUserIds]);

  const handleCardClick = useCallback(
    (userId: number) => {
      if (canReview && activeTab === 'pending') {
        toggleSelect(userId);
        return;
      }
      navigate(`/users/${userId}`);
    },
    [activeTab, canReview, navigate, toggleSelect]
  );

  const handleSent = useCallback(
    async (result: { approvalStatus: SuggestionApprovalStatus }) => {
      setToast(toastForApprovalStatus(result.approvalStatus));
      const nextTab = result.approvalStatus === 'pending' ? 'pending' : 'approved';
      if (nextTab !== activeTab) {
        setActiveTab(nextTab);
      } else {
        await loadSuggestions();
      }
    },
    [activeTab, loadSuggestions]
  );

  const handleReview = useCallback(
    async (action: 'approve' | 'reject') => {
      if (selectedIds.size === 0 || !clientUserId) return;
      setIsReviewing(true);
      setError(null);
      const ids = Array.from(selectedIds);
      try {
        const data =
          action === 'approve'
            ? await suggestionService.approveSuggestions(clientUserId, {
                user_ids: ids,
              })
            : await suggestionService.rejectSuggestions(clientUserId, {
                user_ids: ids,
              });

        setToast({
          title: action === 'approve' ? 'Suggestions approved' : 'Suggestions rejected',
          message:
            action === 'approve'
              ? `${data.approvedCount ?? ids.length} profile(s) approved. The client will be notified by email.`
              : `${data.rejectedCount ?? ids.length} profile(s) rejected.`,
        });
        await loadSuggestions();
      } catch (err) {
        setError(
          (err as { message?: string })?.message ??
            `Failed to ${action} suggestions`
        );
      } finally {
        setIsReviewing(false);
      }
    },
    [clientUserId, loadSuggestions, selectedIds]
  );

  const showReviewActions = canReview && activeTab === 'pending';

  return (
    <>
      <section className="client-profile-card client-profile-card--matches rm-suggestions-tab">
        <div className="client-profile-card__header">
          <h2>RM Suggestions</h2>
          {canSend && (
            <button
              type="button"
              className="rm-suggestions-tab__btn rm-suggestions-tab__btn--approve"
              onClick={() => setIsSuggestModalOpen(true)}
            >
              <PlusIcon /> Suggest New Profile
            </button>
          )}
        </div>

        <nav className="rm-suggestions-tab__tabs" aria-label="Suggestion status">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`rm-suggestions-tab__tab${
                activeTab === tab.key ? ' rm-suggestions-tab__tab--active' : ''
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {error && <p className="rm-suggestions-tab__error">{error}</p>}

        <div className="rm-suggestions-tab__content">
          {showReviewActions && (
            <div className="rm-suggestions-tab__review-bar">
              <label className="rm-suggestions-tab__select-all">
                <input
                  type="checkbox"
                  checked={allPendingSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = somePendingSelected && !allPendingSelected;
                  }}
                  onChange={toggleSelectAll}
                  disabled={isLoading || rows.length === 0}
                  aria-label="Select all pending suggestions"
                />
                <span>
                  {selectedIds.size > 0
                    ? `${selectedIds.size} selected`
                    : 'Select all'}
                </span>
              </label>
              <div className="rm-suggestions-tab__actions">
                <button
                  type="button"
                  className="rm-suggestions-tab__btn rm-suggestions-tab__btn--approve"
                  disabled={selectedIds.size === 0 || isReviewing}
                  onClick={() => handleReview('approve')}
                >
                  {isReviewing ? 'Processing…' : 'Approve selected'}
                </button>
                <button
                  type="button"
                  className="rm-suggestions-tab__btn rm-suggestions-tab__btn--reject"
                  disabled={selectedIds.size === 0 || isReviewing}
                  onClick={() => handleReview('reject')}
                >
                  Reject selected
                </button>
              </div>
            </div>
          )}

          {isLoading && (
            <p className="rm-suggestions-tab__status">Loading suggestions…</p>
          )}

          {!isLoading && rows.length === 0 && (
            <p className="rm-suggestions-tab__empty">
              {emptyMessageForTab(activeTab, isRm)}
            </p>
          )}

          {!isLoading && rows.length > 0 && (
            <div className="client-match-grid">
              {rows.map((row, index) => {
                const profile = mapSuggestedProfileRow(row.suggestedProfile);
                const suggestedUserId = Number(row.suggestedProfile.userId);
                const isSelected = selectedIds.has(suggestedUserId);
                const suggestedBy = formatSuggestedByName(
                  row.suggestedBy.firstName,
                  row.suggestedBy.lastName
                );
                const reviewedBy = row.reviewedBy
                  ? formatSuggestedByName(
                      row.reviewedBy.firstName,
                      row.reviewedBy.lastName
                    )
                  : null;
                const showCheckbox = canReview && activeTab === 'pending';
                const isNavigable = !showCheckbox;

                const metaLine =
                  activeTab === 'pending'
                    ? `Suggested by ${suggestedBy} · ${formatSuggestionDate(row.createdAt)}`
                    : reviewedBy
                      ? `Reviewed by ${reviewedBy} · ${formatSuggestionDate(row.reviewedAt)}`
                      : `Suggested by ${suggestedBy} · ${formatSuggestionDate(row.createdAt)}`;

                return (
                  <article
                    key={row.profileSuggestionId}
                    className={`client-match-card rm-suggestions-tab__card${
                      isSelected ? ' rm-suggestions-tab__card--selected' : ''
                    }${
                      showCheckbox
                        ? ' rm-suggestions-tab__card--selectable'
                        : isNavigable
                          ? ' rm-suggestions-tab__card--navigable'
                          : ''
                    }`}
                    onClick={() => handleCardClick(suggestedUserId)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      handleCardClick(suggestedUserId);
                    }}
                    role={showCheckbox ? 'button' : isNavigable ? 'link' : undefined}
                    tabIndex={showCheckbox || isNavigable ? 0 : undefined}
                  >
                    {showCheckbox && (
                      <label
                        className="rm-suggestions-tab__checkbox"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(suggestedUserId)}
                          aria-label={`Select ${profile.name}`}
                        />
                      </label>
                    )}
                    <div
                      className={`client-match-card__avatar client-match-card__avatar--${
                        (index % 4) + 1
                      }`}
                    >
                      <img
                        src={profile.avatar || ASSETS.images.defaultAvatar}
                        alt=""
                      />
                    </div>
                    <div>
                      <h3>{profile.name}</h3>
                      <p>{profile.meta}</p>
                      <span>{metaLine}</span>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <SuggestProfileModal
        isOpen={isSuggestModalOpen}
        targetUserId={clientUserId}
        clientName={clientName}
        onClose={() => setIsSuggestModalOpen(false)}
        onSent={handleSent}
      />

      {toast && (
        <SubscriptionToast
          title={toast.title}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
